import React, { useState, useMemo } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Breadcrumbs,
  Link,
  Autocomplete,
  TextField
} from '@mui/material';
import { Menu, NavigateNext } from '@mui/icons-material';
import { useLocation, useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import Sidebar from '../Navigation/Sidebar';
import { useCustomers, useCustomerSites } from '../../hooks/useApiQueries';

interface LayoutProps {
  children: React.ReactNode;
  title?: string;
}

const Layout: React.FC<LayoutProps> = ({ children, title }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  // Use React Query hooks for data fetching with automatic caching
  const { data: customers = [], isLoading: isLoadingCustomers } = useCustomers();
  const { data: customerSites = [], isLoading: isLoadingSites } = useCustomerSites(params.customerId);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  // Helper functions to get names from IDs - now using cached data
  const getCustomerName = (customerId: string): string => {
    const customer = customers.find(c => c.ID === customerId);
    return customer ? customer.Name : `Customer ${customerId}`;
  };

  const getSiteName = (customerId: string, siteId: string): string => {
    const site = customerSites.find(s => s.ID === siteId);
    return site ? site.Name : `Site ${siteId}`;
  };

  // Get current customer and site objects
  // If data is loaded, use the full object; otherwise, create a temporary object from URL params
  const currentCustomer = useMemo(() => {
    if (params.customerId) {
      const found = customers.find(c => c.ID === params.customerId);
      if (found) return found;
      // If not found but we have an ID, create temporary object for display
      if (isLoadingCustomers || customers.length === 0) {
        return { ID: params.customerId, Name: params.customerId, CreatedAt: '', UpdatedAt: '' };
      }
    }
    return undefined;
  }, [customers, params.customerId, isLoadingCustomers]);

  const currentSite = useMemo(() => {
    if (params.siteId) {
      const found = customerSites.find(s => s.ID === params.siteId);
      if (found) return found;
      // If not found but we have an ID, create temporary object for display
      if (isLoadingSites || customerSites.length === 0) {
        return { ID: params.siteId, Name: params.siteId, CustomerID: params.customerId || '', CreatedAt: '', UpdatedAt: '' };
      }
    }
    return undefined;
  }, [customerSites, params.siteId, params.customerId, isLoadingSites]);

  // Handle customer selection
  const handleCustomerChange = (_event: any, newValue: any) => {
    if (newValue) {
      navigate(`/customers/${newValue.ID}/sites`);
    }
  };

  // Handle site selection
  const handleSiteChange = (_event: any, newValue: any) => {
    if (newValue && params.customerId) {
      navigate(`/customers/${params.customerId}/sites/${newValue.ID}`);
    }
  };

  // Determine breadcrumb structure
  const showCustomerSelector = params.customerId !== undefined;
  const showSiteSelector = params.siteId !== undefined;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <Sidebar
        open={sidebarOpen}
        onClose={handleSidebarClose}
        variant={isMobile ? 'temporary' : 'permanent'}
      />

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          ml: isMobile ? 0 : 0, // Sidebar handles its own width
          transition: theme.transitions.create(['margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {/* Top Bar */}
        <AppBar
          position="sticky"
          elevation={1}
          sx={{
            backgroundColor: 'background.paper',
            color: 'text.primary',
            borderBottom: '1px solid',
            borderColor: 'divider',
            zIndex: theme.zIndex.drawer - 1,
          }}
        >
          <Toolbar sx={{ minHeight: '64px !important' }}>
            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                edge="start"
                onClick={handleSidebarToggle}
                sx={{ mr: 2 }}
              >
                <Menu />
              </IconButton>
            )}

            {/* Navigation Breadcrumbs with Dropdowns */}
            <Box sx={{ flexGrow: 1, ml: 3, display: 'flex', alignItems: 'center' }}>
              <Breadcrumbs
                separator={<NavigateNext fontSize="small" />}
                sx={{ fontSize: '1.25rem' }}
              >
                {/* Customers Link */}
                {location.pathname === '/customers' ? (
                  <Typography
                    color="text.primary"
                    sx={{ fontSize: '1.25rem', fontWeight: 600 }}
                  >
                    Customers
                  </Typography>
                ) : location.pathname === '/distributed-agents' ? (
                  <Typography
                    color="text.primary"
                    sx={{ fontSize: '1.25rem', fontWeight: 600 }}
                  >
                    Distributed Agents
                  </Typography>
                ) : (
                  <Link
                    component={RouterLink}
                    to="/customers"
                    color="inherit"
                    sx={{ 
                      textDecoration: 'none',
                      fontSize: '1.25rem',
                      fontWeight: 500,
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    Customers
                  </Link>
                )}

                {/* Customer Selector Dropdown */}
                {showCustomerSelector && (
                  <Autocomplete
                    options={customers}
                    getOptionLabel={(option) => option.Name}
                    value={currentCustomer || undefined}
                    onChange={handleCustomerChange}
                    isOptionEqualToValue={(option, value) => option.ID === value?.ID}
                    disableClearable
                    disabled={isLoadingCustomers}
                    loading={isLoadingCustomers}
                    size="small"
                    sx={{ 
                      minWidth: 200,
                      '& .MuiOutlinedInput-root': {
                        fontSize: '1.25rem',
                        fontWeight: 500,
                        padding: '2px 8px',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        borderRadius: 1,
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.08)'
                        },
                        '& fieldset': {
                          border: '1px solid rgba(0, 0, 0, 0.23)',
                          borderRadius: 1
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.4)'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: '2px'
                        }
                      },
                      '& .MuiAutocomplete-input': {
                        padding: '0 !important'
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder={isLoadingCustomers ? 'Loading...' : 'Select customer'}
                      />
                    )}
                  />
                )}

                {/* Sites Link */}
                {showCustomerSelector && !showSiteSelector && (
                  <Typography
                    color="text.primary"
                    sx={{ fontSize: '1.25rem', fontWeight: 600 }}
                  >
                    Sites
                  </Typography>
                )}
                {showSiteSelector && (
                  <Link
                    component={RouterLink}
                    to={`/customers/${params.customerId}/sites`}
                    color="inherit"
                    sx={{ 
                      textDecoration: 'none',
                      fontSize: '1.25rem',
                      fontWeight: 500,
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    Sites
                  </Link>
                )}

                {/* Site Selector Dropdown */}
                {showSiteSelector && (
                  <Autocomplete
                    options={customerSites}
                    getOptionLabel={(option) => option.Name}
                    value={currentSite || undefined}
                    onChange={handleSiteChange}
                    isOptionEqualToValue={(option, value) => option.ID === value?.ID}
                    disableClearable
                    disabled={isLoadingSites}
                    loading={isLoadingSites}
                    size="small"
                    sx={{ 
                      minWidth: 200,
                      '& .MuiOutlinedInput-root': {
                        fontSize: '1.25rem',
                        fontWeight: 500,
                        padding: '2px 8px',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        borderRadius: 1,
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.08)'
                        },
                        '& fieldset': {
                          border: '1px solid rgba(0, 0, 0, 0.23)',
                          borderRadius: 1
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.4)'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: '2px'
                        }
                      },
                      '& .MuiAutocomplete-input': {
                        padding: '0 !important'
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder={isLoadingSites ? 'Loading...' : 'Select site'}
                      />
                    )}
                  />
                )}
              </Breadcrumbs>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Page Content */}
        <Box
          sx={{
            flexGrow: 1,
            backgroundColor: 'background.default',
            minHeight: 'calc(100vh - 64px)', // Subtract top bar height
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
